import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  // Company Settings State
  const [companySettings, setCompanySettings] = useState({
    companyName: 'BookYatri',
    supportName: 'Customer Support Team',
    supportEmail: 'support@bookyatri.com',
    supportPhone: '+91-9876543210',
    companyEmail: 'info@bookyatri.com',
    companyPhone: '+91-9876543211',
    address: '123, Connaught Place, New Delhi - 110001',
    city: 'New Delhi',
    state: 'Delhi',
    country: 'India',
    pincode: '110001',
    website: 'https://www.bookyatri.com',
    gstNumber: '07ABCDE1234F1Z5',
    panNumber: 'ABCDE1234F',
    cinNumber: 'U74999DL2020PTC123456',
    logo: '/storage/uploads/company/logos/logo.png', // BookYatri logo from company logos folder
    favicon: null
  });

  // Bank Accounts State
  const [bankAccounts, setBankAccounts] = useState([
    {
      id: 'BA001',
      bankName: 'State Bank of India',
      accountNumber: '1234567890123456',
      accountHolderName: 'BookYatri',
      ifscCode: 'SBIN0001234',
      branchName: 'Connaught Place',
      branchAddress: '123, Connaught Place, New Delhi - 110001',
      accountType: 'Current',
      status: 'Active',
      isDefault: true,
      upiId: 'bookyatri@sbi'
    }
  ]);

  // User Management State
  const [users, setUsers] = useState([
    {
      id: 'USR001',
      username: 'admin',
      email: 'admin@bookyatri.com',
      firstName: 'John',
      lastName: 'Doe',
      role: 'Super Admin',
      status: 'Active'
    }
  ]);

  // Website Settings State
  const [websiteSettings, setWebsiteSettings] = useState({
    siteTitle: 'BookYatri - Your Travel Partner',
    siteDescription: 'Book flights, hotels, buses, trains and holiday packages with best deals',
    siteKeywords: 'travel, flights, hotels, buses, trains, holiday packages, booking',
    maintenanceMode: false,
    allowRegistration: true,
    allowGuestBooking: true,
    defaultCurrency: 'INR',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h'
  });

  // Slider Management State
  const [sliders, setSliders] = useState([
    {
      id: 'SLD001',
      title: 'Welcome to BookYatri',
      subtitle: 'Your trusted travel partner',
      image: '/storage/uploads/sliders/images/slider-0-welcome-bookyatri.jpg',
      link: '/',
      isActive: true,
      targetAudience: 'B2C'
    }
  ]);

  // Offer Management State
  const [offers, setOffers] = useState([
    {
      id: 'OFF001',
      title: 'Summer Sale - Up to 50% Off',
      description: 'Book your summer vacation with amazing discounts',
      image: '/storage/uploads/offers/images/offers-default.jpg',
      link: '/offers/summer-sale',
      isActive: true,
      validFrom: '2024-01-01',
      validTo: '2024-12-31'
    }
  ]);

  // Page Management State
  const [pages, setPages] = useState([
    {
      id: 'PAGE001',
      title: 'About Us',
      slug: 'about',
      content: 'We are a leading travel booking platform...',
      isActive: true,
      metaTitle: 'About BookYatri',
      metaDescription: 'Learn about BookYatri travel services'
    }
  ]);

  // Blog Management State
  const [blogs, setBlogs] = useState([
    {
      id: 'BLOG001',
      title: 'Top 10 Travel Destinations in 2024',
      slug: 'top-10-travel-destinations-2024',
      content: 'Discover the most amazing places to visit this year...',
      excerpt: 'Explore the best travel destinations for 2024',
      author: 'Travel Expert',
      publishedAt: '2024-01-15',
      isActive: true,
      featuredImage: '/storage/uploads/blog/images/blog-default-featured.jpg'
    }
  ]);

  // Newsletter Subscribers State
  const [newsletterSubscribers, setNewsletterSubscribers] = useState([
    {
      id: 'SUB001',
      email: 'user@example.com',
      subscribedAt: '2024-01-15',
      isActive: true
    }
  ]);

  // Map Settings (global)
  const [mapSettings, setMapSettings] = useState({
    provider: 'google', // 'google' | 'local'
    googleApiKey: '',
    defaultCenter: { lat: '', lng: '' },
    defaultZoom: 12
  });

  // Flight API Integrations (e.g., Amadeus, Sabre, Travelport)
  const [flightApiConfig, setFlightApiConfig] = useState({
    amadeus: {
      enabled: false,
      clientId: '',
      clientSecret: '',
      environment: 'test' // 'test' | 'production'
    },
    sabre: {
      enabled: false,
      clientId: '',
      clientSecret: '',
      environment: 'test'
    },
    travelport: {
      enabled: false,
      clientId: '',
      clientSecret: '',
      environment: 'test'
    },
    customApis: [] // { id, name, enabled, baseUrl, apiKey }
  });

  // Feedback State
  const [feedbacks, setFeedbacks] = useState([
    {
      id: 'FB001',
      name: 'John Doe',
      email: 'john@example.com',
      subject: 'Great Service',
      message: 'Excellent booking experience',
      rating: 5,
      createdAt: '2024-01-15',
      status: 'New'
    }
  ]);

  // Convenience Fee State
  const [convenienceFees, setConvenienceFees] = useState([
    {
      id: 'CF001',
      paymentGateway: 'Razorpay',
      service: 'Flight',
      customerType: 'B2C',
      amountRange: '0-10000',
      paymentMode: 'Credit Card',
      amountType: 'Fixed',
      amount: 50,
      isActive: true
    }
  ]);

  // Online Transactions State
  const [onlineTransactions, setOnlineTransactions] = useState([
    {
      id: 'TXN001',
      transactionId: 'TXN123456789',
      amount: 5000,
      currency: 'INR',
      paymentGateway: 'Razorpay',
      status: 'Success',
      customerName: 'John Doe',
      customerEmail: 'john@example.com',
      bookingId: 'BK001',
      createdAt: '2024-01-15T10:30:00Z'
    }
  ]);

  // Payment History State
  const [paymentHistory, setPaymentHistory] = useState([
    {
      id: 'PH001',
      requestId: 'REQ123456789',
      amount: 10000,
      companyName: 'Travel Agency Ltd',
      requestDate: '2024-01-15',
      status: 'Pending',
      approvedBy: null,
      approvedAt: null
    }
  ]);

  // Credit Notes State
  const [creditNotes, setCreditNotes] = useState([
    {
      id: 'CN001',
      creditNoteNumber: 'CN-2024-001',
      bookingId: 'BK001',
      customerName: 'John Doe',
      refundAmount: 2500,
      cancellationAmount: 500,
      totalCreditAmount: 3000,
      reason: 'Flight Cancellation',
      status: 'Generated',
      createdAt: '2024-01-15'
    }
  ]);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Load data from API and localStorage on component mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    // Fetch company settings from API
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/company`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            console.log('AdminContext: Loading company settings from API:', data.data);
            setCompanySettings(prev => ({ ...prev, ...data.data }));
          }
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
        // Fallback to localStorage
        const savedCompanySettings = localStorage.getItem('companySettings');
        if (savedCompanySettings) {
          const parsed = JSON.parse(savedCompanySettings);
          setCompanySettings(parsed);
        }
      }
    };

    // Fetch bank accounts from API
    const fetchBankAccounts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/bank-accounts`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setBankAccounts(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching bank accounts:', error);
        // Fallback to localStorage
        const savedBankAccounts = localStorage.getItem('bankAccounts');
        if (savedBankAccounts) {
          setBankAccounts(JSON.parse(savedBankAccounts));
        }
      }
    };

    // Fetch website settings from API
    const fetchWebsiteSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/website`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setWebsiteSettings(prev => ({ ...prev, ...data.data }));
          }
        }
      } catch (error) {
        console.error('Error fetching website settings:', error);
        // Fallback to localStorage
        const savedWebsiteSettings = localStorage.getItem('websiteSettings');
        if (savedWebsiteSettings) {
          setWebsiteSettings(JSON.parse(savedWebsiteSettings));
        }
      }
    };

    // Fetch sliders from API
    const fetchSliders = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/sliders/all`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setSliders(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching sliders:', error);
        // Fallback to localStorage
        const savedSliders = localStorage.getItem('sliders');
        if (savedSliders) {
          setSliders(JSON.parse(savedSliders));
        }
      }
    };

    // Fetch offers from API
    const fetchOffers = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/settings/offers/all`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setOffers(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching offers:', error);
        // Fallback to localStorage
        const savedOffers = localStorage.getItem('offers');
        if (savedOffers) {
          setOffers(JSON.parse(savedOffers));
        }
      }
    };

    // Fetch all data
    fetchCompanySettings();
    fetchBankAccounts();
    fetchWebsiteSettings();
    fetchSliders();
    fetchOffers();

    // Also check uploaded files
    const uploadedFiles = localStorage.getItem('uploadedFiles');
    if (uploadedFiles) {
      console.log('AdminContext: Uploaded files:', JSON.parse(uploadedFiles));
    }

    const savedConvenienceFees = localStorage.getItem('convenienceFees');
    if (savedConvenienceFees) {
      setConvenienceFees(JSON.parse(savedConvenienceFees));
    }

    const savedOnlineTransactions = localStorage.getItem('onlineTransactions');
    if (savedOnlineTransactions) {
      setOnlineTransactions(JSON.parse(savedOnlineTransactions));
    }

    const savedPaymentHistory = localStorage.getItem('paymentHistory');
    if (savedPaymentHistory) {
      setPaymentHistory(JSON.parse(savedPaymentHistory));
    }

    const savedCreditNotes = localStorage.getItem('creditNotes');
    if (savedCreditNotes) {
      setCreditNotes(JSON.parse(savedCreditNotes));
    }

    const savedFlightApiConfig = localStorage.getItem('flightApiConfig');
    if (savedFlightApiConfig) {
      setFlightApiConfig(JSON.parse(savedFlightApiConfig));
    }

    const savedMapSettings = localStorage.getItem('mapSettings');
    if (savedMapSettings) {
      setMapSettings(JSON.parse(savedMapSettings));
    }
  }, []);

  // Save to localStorage whenever state changes
  useEffect(() => {
    console.log('AdminContext: Saving company settings to localStorage:', companySettings);
    localStorage.setItem('companySettings', JSON.stringify(companySettings));
  }, [companySettings]);

  useEffect(() => {
    localStorage.setItem('bankAccounts', JSON.stringify(bankAccounts));
  }, [bankAccounts]);

  useEffect(() => {
    localStorage.setItem('websiteSettings', JSON.stringify(websiteSettings));
  }, [websiteSettings]);

  useEffect(() => {
    localStorage.setItem('sliders', JSON.stringify(sliders));
  }, [sliders]);

  useEffect(() => {
    localStorage.setItem('offers', JSON.stringify(offers));
  }, [offers]);

  useEffect(() => {
    localStorage.setItem('convenienceFees', JSON.stringify(convenienceFees));
  }, [convenienceFees]);

  useEffect(() => {
    localStorage.setItem('onlineTransactions', JSON.stringify(onlineTransactions));
  }, [onlineTransactions]);

  useEffect(() => {
    localStorage.setItem('paymentHistory', JSON.stringify(paymentHistory));
  }, [paymentHistory]);

  useEffect(() => {
    localStorage.setItem('creditNotes', JSON.stringify(creditNotes));
  }, [creditNotes]);

  useEffect(() => {
    localStorage.setItem('flightApiConfig', JSON.stringify(flightApiConfig));
  }, [flightApiConfig]);

  useEffect(() => {
    localStorage.setItem('mapSettings', JSON.stringify(mapSettings));
  }, [mapSettings]);

  // Company Settings Functions
  const updateCompanySettings = async (newSettings) => {
    console.log('AdminContext: Updating company settings:', newSettings);
    setCompanySettings(prev => ({ ...prev, ...newSettings }));
    
    // Also update in API
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/settings/company`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
    } catch (error) {
      console.error('Error updating company settings in API:', error);
    }
  };

  // Test function to set a sample logo (for debugging)
  const setTestLogo = () => {
    const testLogo = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iIzAwN0JGRiIvPgogIDx0ZXh0IHg9IjUwIiB5PSI1NSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+TE9HTzwvdGV4dD4KPC9zdmc+';
    console.log('Setting test logo:', testLogo);
    updateCompanySettings({ logo: testLogo });
  };

  // Bank Account Functions
  const addBankAccount = (account) => {
    setBankAccounts(prev => [...prev, account]);
  };

  const updateBankAccount = (id, updates) => {
    setBankAccounts(prev => prev.map(account => 
      account.id === id ? { ...account, ...updates } : account
    ));
  };

  const deleteBankAccount = (id) => {
    setBankAccounts(prev => prev.filter(account => account.id !== id));
  };

  const setDefaultBankAccount = (id) => {
    setBankAccounts(prev => prev.map(account => ({
      ...account,
      isDefault: account.id === id
    })));
  };

  // User Management Functions
  const addUser = (user) => {
    setUsers(prev => [...prev, user]);
  };

  const updateUser = (id, updates) => {
    setUsers(prev => prev.map(user => 
      user.id === id ? { ...user, ...updates } : user
    ));
  };

  const deleteUser = (id) => {
    setUsers(prev => prev.filter(user => user.id !== id));
  };

  // Website Settings Functions
  const updateWebsiteSettings = async (newSettings) => {
    setWebsiteSettings(prev => ({ ...prev, ...newSettings }));
    
    // Also update in API
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/settings/website`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newSettings)
      });
    } catch (error) {
      console.error('Error updating website settings in API:', error);
    }
  };

  // Slider Management Functions
  const addSlider = (slider) => {
    setSliders(prev => [...prev, slider]);
  };

  const updateSlider = (id, updates) => {
    setSliders(prev => prev.map(slider => 
      slider.id === id ? { ...slider, ...updates } : slider
    ));
  };

  const deleteSlider = (id) => {
    setSliders(prev => prev.filter(slider => slider.id !== id));
  };

  // Offer Management Functions
  const addOffer = (offer) => {
    setOffers(prev => [...prev, offer]);
  };

  const updateOffer = (id, updates) => {
    setOffers(prev => prev.map(offer => 
      offer.id === id ? { ...offer, ...updates } : offer
    ));
  };

  const deleteOffer = (id) => {
    setOffers(prev => prev.filter(offer => offer.id !== id));
  };

  // Page Management Functions
  const addPage = (page) => {
    setPages(prev => [...prev, page]);
  };

  const updatePage = (id, updates) => {
    setPages(prev => prev.map(page => 
      page.id === id ? { ...page, ...updates } : page
    ));
  };

  const deletePage = (id) => {
    setPages(prev => prev.filter(page => page.id !== id));
  };

  // Blog Management Functions
  const addBlog = (blog) => {
    setBlogs(prev => [...prev, blog]);
  };

  const updateBlog = (id, updates) => {
    setBlogs(prev => prev.map(blog => 
      blog.id === id ? { ...blog, ...updates } : blog
    ));
  };

  const deleteBlog = (id) => {
    setBlogs(prev => prev.filter(blog => blog.id !== id));
  };

  // Newsletter Functions
  const addNewsletterSubscriber = (subscriber) => {
    setNewsletterSubscribers(prev => [...prev, subscriber]);
  };

  const removeNewsletterSubscriber = (id) => {
    setNewsletterSubscribers(prev => prev.filter(sub => sub.id !== id));
  };

  // Feedback Functions
  const addFeedback = (feedback) => {
    setFeedbacks(prev => [...prev, feedback]);
  };

  const updateFeedback = (id, updates) => {
    setFeedbacks(prev => prev.map(feedback => 
      feedback.id === id ? { ...feedback, ...updates } : feedback
    ));
  };

  const deleteFeedback = (id) => {
    setFeedbacks(prev => prev.filter(feedback => feedback.id !== id));
  };

  // Flight API Config Functions
  const updateFlightApiConfig = (updates) => {
    setFlightApiConfig(prev => ({ ...prev, ...updates }));
  };

  const addCustomFlightApi = (api) => {
    setFlightApiConfig(prev => ({
      ...prev,
      customApis: [...prev.customApis, { ...api, id: api.id || Date.now().toString() }]
    }));
  };

  const updateCustomFlightApi = (id, updates) => {
    setFlightApiConfig(prev => ({
      ...prev,
      customApis: prev.customApis.map(a => a.id === id ? { ...a, ...updates } : a)
    }));
  };

  const deleteCustomFlightApi = (id) => {
    setFlightApiConfig(prev => ({
      ...prev,
      customApis: prev.customApis.filter(a => a.id !== id)
    }));
  };

  // Map Settings functions
  const updateMapSettings = (updates) => {
    setMapSettings(prev => ({ ...prev, ...updates }));
  };

  // Getter Functions for Public Website
  const getActiveSliders = () => {
    return sliders.filter(slider => slider.isActive);
  };

  const getActiveOffers = () => {
    const now = new Date();
    return offers.filter(offer => 
      offer.isActive && 
      new Date(offer.validFrom) <= now && 
      new Date(offer.validTo) >= now
    );
  };

  const getActivePages = () => {
    return pages.filter(page => page.isActive);
  };

  const getActiveBlogs = () => {
    return blogs.filter(blog => blog.isActive);
  };

  const getDefaultBankAccount = () => {
    return bankAccounts.find(account => account.isDefault && account.status === 'Active');
  };

  const getActiveNewsletterSubscribers = () => {
    return newsletterSubscribers.filter(sub => sub.isActive);
  };

  const getNewFeedbacks = () => {
    return feedbacks.filter(feedback => feedback.status === 'New');
  };

  const getConvenienceFees = () => {
    return convenienceFees.filter(fee => fee.isActive);
  };

  const getOnlineTransactions = () => {
    return onlineTransactions;
  };

  const getPaymentHistory = () => {
    return paymentHistory;
  };

  const getCreditNotes = () => {
    return creditNotes;
  };

  const value = {
    // State
    companySettings,
    bankAccounts,
    users,
    websiteSettings,
    sliders,
    offers,
    pages,
    blogs,
    newsletterSubscribers,
    feedbacks,
    convenienceFees,
    onlineTransactions,
    paymentHistory,
    creditNotes,
    flightApiConfig,
    mapSettings,

    // Company Settings
    updateCompanySettings,
    setTestLogo,

    // Bank Accounts
    addBankAccount,
    updateBankAccount,
    deleteBankAccount,
    setDefaultBankAccount,

    // User Management
    addUser,
    updateUser,
    deleteUser,

    // Website Settings
    updateWebsiteSettings,

    // Slider Management
    addSlider,
    updateSlider,
    deleteSlider,

    // Offer Management
    addOffer,
    updateOffer,
    deleteOffer,

    // Page Management
    addPage,
    updatePage,
    deletePage,

    // Blog Management
    addBlog,
    updateBlog,
    deleteBlog,

    // Newsletter
    addNewsletterSubscriber,
    removeNewsletterSubscriber,

    // Feedback
    addFeedback,
    updateFeedback,
    deleteFeedback,

    // Flight API Config
    updateFlightApiConfig,
    setFlightApiConfig,
    addCustomFlightApi,
    updateCustomFlightApi,
    deleteCustomFlightApi,
    updateMapSettings,
    setMapSettings,

    // Getter Functions
    getActiveSliders,
    getActiveOffers,
    getActivePages,
    getActiveBlogs,
    getDefaultBankAccount,
    getActiveNewsletterSubscribers,
    getNewFeedbacks,
    getConvenienceFees,
    getOnlineTransactions,
    getPaymentHistory,
    getCreditNotes
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
